/**
 * Shows "Added to cart" notification on successful add (quickshop or product page).
 * Listens for ThemeEvents.cartUpdate, fetches cart, displays last added item.
 */
import { ThemeEvents } from "@theme/events";

const NOTIFICATION_ID = "added-to-cart-notification";
const AUTO_HIDE_MS = 4000;
const HIDE_ANIMATION_MS = 250;

function getCartUrl() {
  const el = document.getElementById(NOTIFICATION_ID);
  return el?.getAttribute("data-cart-url") || "/cart";
}

function getEl(selector) {
  const root = document.getElementById(NOTIFICATION_ID);
  return root?.querySelector(selector) ?? null;
}

/** Largest rendered dimension of the thumbnail (72x96 box). */
const IMAGE_RENDER_PX = 96;

/**
 * cart.js returns the full-size product image URL, which can be 1500-2048px
 * wide and weigh hundreds of KB. Dropping that into a 72x96 box means the
 * browser downloads the whole thing before painting (the 2-3s delay). Ask the
 * Shopify CDN for a thumbnail scaled to the rendered size (capped at 3x DPR).
 * @param {string} url
 * @returns {string}
 */
function notificationImageUrl(url) {
  if (!url) return "";
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const width = Math.round(IMAGE_RENDER_PX * dpr);
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set("width", String(width));
    return parsed.toString();
  } catch {
    return url;
  }
}

function formatMoney(cents, currency) {
  const code = currency || window.Shopify?.currency?.active || "SEK";
  const locale =
    window.Shopify?.locale || document.documentElement.lang || "en";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${code}`;
  }
}

/**
 * Clears the dynamic content so the next add starts blank. Without this, a
 * re-trigger briefly shows the previously added product's image/title/price
 * until the new cart.js fetch resolves — better to show nothing than the
 * wrong item.
 */
function clearNotificationContent() {
  const imageEl = getEl("[data-notification-image]");
  const titleEl = getEl("[data-notification-title]");
  const priceEl = getEl("[data-notification-price]");
  if (imageEl) {
    imageEl.removeAttribute("src");
    imageEl.alt = "";
  }
  if (titleEl) titleEl.innerHTML = "";
  if (priceEl) priceEl.textContent = "";
}

function hideNotification() {
  const el = document.getElementById(NOTIFICATION_ID);
  if (!el) return;
  el.classList.add("added-to-cart-notification--hiding");
  el.removeAttribute("role");
  setTimeout(() => {
    el.setAttribute("aria-hidden", "true");
    el.classList.remove("added-to-cart-notification--hiding");
    clearNotificationContent();
  }, HIDE_ANIMATION_MS);
}

let hideTimeoutId = null;
/** Track cart item count so we only show on add, not on remove */
let previousItemCount = 0;

function showNotification() {
  const el = document.getElementById(NOTIFICATION_ID);
  if (!el) return;

  el.setAttribute("aria-hidden", "false");
  el.setAttribute("role", "alert");

  if (hideTimeoutId) clearTimeout(hideTimeoutId);
  hideTimeoutId = setTimeout(hideNotification, AUTO_HIDE_MS);
}

async function handleCartUpdate(event) {
  const data = event?.detail?.data;
  if (data?.didError) return;

  const el = document.getElementById(NOTIFICATION_ID);
  if (!el) return;

  // If the toast is still up from a previous add, wipe it now so the old
  // product isn't shown during the cart.js fetch (~0.5s). Blank is better
  // than the wrong item.
  if (el.getAttribute("aria-hidden") === "false") {
    clearNotificationContent();
  }

  try {
    const res = await fetch(`${getCartUrl()}.js`);
    if (!res.ok) return;
    const cart = await res.json();

    const itemCount = cart.item_count ?? 0;
    const wasAdd = itemCount > previousItemCount;
    previousItemCount = itemCount;

    if (!wasAdd) return;

    const imageEl = getEl("[data-notification-image]");
    const titleEl = getEl("[data-notification-title]");
    const priceEl = getEl("[data-notification-price]");

    const items = cart.items ?? [];
    const variantId = event?.detail?.sourceId;
    const addedItem =
      (variantId &&
        items.find((item) => String(item.variant_id) === String(variantId))) ||
      items[items.length - 1];
    if (addedItem) {
      if (imageEl) {
        imageEl.src = notificationImageUrl(addedItem.image);
        imageEl.alt = addedItem.title ?? "";
      }
      if (titleEl) titleEl.innerHTML = addedItem.title ?? "";
      if (priceEl) {
        const price =
          addedItem.final_line_price ??
          addedItem.line_price ??
          addedItem.price * (addedItem.quantity ?? 1);
        priceEl.textContent = formatMoney(price, cart.currency ?? "SEK");
      }
    }

    showNotification();
  } catch {
    const fallbackCount = event?.detail?.cart?.item_count;
    if (typeof fallbackCount === "number") {
      previousItemCount = fallbackCount;
    }
  }
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);

// Initialize previous count so we only show when count increases (add), not when it decreases (remove)
fetch(`${getCartUrl()}.js`)
  .then((res) => (res.ok ? res.json() : null))
  .then((cart) => {
    if (cart) previousItemCount = cart.item_count ?? 0;
  })
  .catch(() => {});

// Hide when user clicks cart link, checkout link, or view-cart button
document.getElementById(NOTIFICATION_ID)?.addEventListener("click", (e) => {
  const target = e.target?.closest?.(
    'a[href*="cart"], a[href*="checkout"], .added-to-cart-notification__view-cart',
  );
  if (target) hideNotification();
});
